/**
 * Мелкие общие элементы модуля «Апартаменты».
 */

import { useState, type ReactNode } from 'react';
import { Building2, Star } from 'lucide-react';
import type { StayPlatform, StayPropertyStatus } from '@tw/shared-types';
import { cn } from '@/lib/utils';
import { PLATFORM, PROPERTY_STATUS } from './meta.ts';

/** Значок площадки: буква на фирменном цвете. Свой, а не логотип — бренды
 *  площадок в интерфейсе не используем (§39 ТЗ, то же правило, что для
 *  Telegram). */
export function PlatformMark({ platform, size = 'md' }: { platform: StayPlatform; size?: 'sm' | 'md' }) {
  return (
    <span
      title={PLATFORM[platform].label}
      style={{ background: PLATFORM[platform].color }}
      className={cn(
        'inline-grid flex-none place-items-center rounded-md font-bold text-white',
        size === 'sm' ? 'size-4 text-[9px]' : 'size-6 text-[11px]',
      )}
    >
      {platform === 'AIRBNB' ? 'A' : 'B'}
    </span>
  );
}

export function StatusPill({ status, className }: { status: StayPropertyStatus; className?: string }) {
  const s = PROPERTY_STATUS[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
        s.tone,
        className,
      )}
    >
      <span className={cn('size-1.5 rounded-full', s.dot)} />
      {s.short}
    </span>
  );
}

/** Средняя оценка: звезда и число. Без оценок — ничего, а не «0». */
export function RatingBadge({ rating, count }: { rating: number | null; count?: number }) {
  if (rating === null) {
    return <span className="text-[12px] text-muted-foreground">нет оценок</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-heading">
      <Star className="size-3.5 fill-amber-400 text-amber-400" />
      {rating.toFixed(1)}
      {count !== undefined && (
        <span className="font-normal text-muted-foreground">· {count}</span>
      )}
    </span>
  );
}

/** Выбор оценки 1–5 звёздами; повторный клик по той же снимает оценку. */
export function RatingInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? value ?? 0;
  return (
    <span className="inline-flex items-center gap-0.5" onMouseLeave={() => setHover(null)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`Оценка ${n}`}
          onMouseEnter={() => setHover(n)}
          onClick={() => onChange(value === n ? null : n)}
          className="rounded border-0 bg-transparent p-0.5 transition-transform hover:scale-110 hover:bg-transparent"
        >
          <Star
            className={cn(
              'size-5 transition-colors',
              n <= shown ? 'fill-amber-400 text-amber-400' : 'text-border-strong',
            )}
          />
        </button>
      ))}
    </span>
  );
}

/** Фото квартиры или аккуратная заглушка с цветом площадки. */
export function PropertyThumb({
  propertyId,
  photoUrl,
  platform,
  className,
}: {
  propertyId: string;
  photoUrl: string | null;
  platform: StayPlatform | null;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (photoUrl && !failed) {
    // Через наш API: интерфейс показывает картинки только со своего адреса.
    // Адрес фото — в параметре, чтобы смена фото не упиралась в кэш браузера.
    return (
      <img
        src={`/api/stays/properties/${propertyId}/photo?v=${encodeURIComponent(photoUrl)}`}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className={cn('object-cover', className)}
      />
    );
  }
  const color = platform ? PLATFORM[platform].color : 'var(--accent)';
  return (
    <span
      className={cn('grid place-items-center', className)}
      style={{ background: `linear-gradient(135deg, ${color}22, ${color}0a)` }}
    >
      <Building2 className="size-1/3 max-h-10 max-w-10" style={{ color }} strokeWidth={1.5} />
    </span>
  );
}

export function FormField({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex flex-col gap-1.5', className)}>
      <span className="text-[12px] font-medium text-soft">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

/** Поле ввода в стиле модуля — для нативных input/select/textarea. */
export const INPUT =
  'h-9 w-full rounded-lg border border-border bg-card px-3 text-[13px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-60';

export const TEXTAREA = INPUT.replace('h-9', 'min-h-[76px] py-2 leading-relaxed');

export function EmptyState({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-card/50 px-6 py-14 text-center">
      <span className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-brand">{icon}</span>
      <p className="text-[15px] font-semibold text-heading">{title}</p>
      {children && <div className="max-w-[44ch] text-[13px] leading-relaxed text-muted-foreground">{children}</div>}
    </div>
  );
}
